// ============================================
// Interactive Location Views
// Replaces all ViewControllers (Apartment, Beach, Gym, Clinic, Club, Mall)
// ============================================

import { events, Events } from '../engine/events.js';
import { Location } from '../engine/constants.js';
import { isTarMode, getImageUrl } from '../image-loader.js';
import { appearanceState } from '../systems/appearance.js';
import { L } from '../systems/localization.js';
import { getEffectiveFemGain, getEffectiveSubGain } from '../engine/externals.js';

// ── Ink knot → Location key mapping ────────────────────────────────────
// StoryManager.currentLocation stores the Ink knot name (e.g. "Apartment", "BarEntry")
// We need to map back to Location constants for hotspot lookup
const KNOT_TO_LOCATION = {
    'Apartment':  Location.HOME,
    'Basement':   'Basement',
    'Playroom':   'Playroom',
    // Construction Site hub (masc-route job track, 0.2.8). Without this the
    // 'Construction' map def (map_construction_day + 5 hotspots) never
    // rendered — the #location: tag is informational-only (tags.js no-op)
    // and location detection runs off the knot name. Caught 2026-06-10
    // while shooting the feature-howto screenshots.
    'ConstructionSite_Hub': 'Construction',
    'BarEntry':   Location.BAR,
    'BarEntry_Storage': Location.BAR,
    'Bar':        Location.BAR,
    'ParkEntry':  Location.PARK,
    'Park':       Location.PARK,
    'MallEntry':  Location.MALL,
    'Mall':       Location.MALL,
    'Outside':        'Outside',
    'OutsideChoices': 'Outside',
    'SalonEntry':     Location.SALON,
    'Salon':          Location.SALON,
    'Salon_Interior': Location.SALON,
    'BeachEntry': Location.BEACH,
    'Beach':      Location.BEACH,
    'ClubEntry':  Location.CLUB,
    'Club':       Location.CLUB,
    'ClubInside': Location.CLUB,
    'GymEntry':   Location.GYM,
    'Gym':        Location.GYM,
    'GymHub':     Location.GYM,
    'ClinicEntry':Location.CLINIC,
    'Clinic':     Location.CLINIC,
    'ClinicHub':  Location.CLINIC,
    'SpaEntry':   'Spa',
    'Spa':        'Spa',
    'SpaHub':     'Spa',
    'TattooEntry': Location.TATTOO,
    'TattooHub':   Location.TATTOO,
    'TattooMenu':  Location.TATTOO,
    // Two-City
    'Downtown':         'Downtown',
    'RedLight':         'RedLight',
    'RedLightEntry':    'RedLight',
    'CornerStoreEntry': 'CornerStore',
    'CornerStore':      'CornerStore',
    'Brothel':          'Brothel',
    'BrothelEntry':     'Brothel',
    // The Gilded Cage cabaret (0.3, armed, gated on v030_content_enabled)
    'CabaretEntry':     Location.CABARET,
    'CabaretHub':       Location.CABARET,
    'MaidCafe':         'MaidCafe',
    'MaidCafeEntry':    'MaidCafe',
    'Office_Entry': 'Office',
    'Office_Hub':   'Office',
    'Office':       'Office',
    'University_Entry': 'University',
    'University_OpenLecture': 'University',
    'University_Lecture_SignUp': 'University',
    'BNWO_Doubt':       'University',
    'BNWO_Doubt_Return': 'University',
    'BNWO_Hub':         'University',
    'Maid_Entry':   'Maid',
    'Maid':         'Maid',
    // Cove Resort (v0.2.5)
    'Resort_Travel_Entry': 'Resort',
    'Resort':              'Resort',
    'Resort_Lobby':        'Resort_Lobby',
    'Resort_Lobby_Booking':    'Resort_Lobby',
    'Resort_Lobby_Boutique':   'Resort_Lobby',
    'Resort_Lobby_CheckOut':   'Resort_Lobby',
    'Resort_Lobby_Extend':     'Resort_Lobby',
    'Resort_PoolDeck':     'Resort_PoolDeck',
    'Resort_TikiBar':      'Resort_TikiBar',
    'Resort_Cabana':       'Resort_Cabana',
    'Resort_Cabana_Rent':  'Resort_Cabana',
    'Resort_Beach':        'Resort_Beach',
    'Resort_Beach_Sunset': 'Resort_Beach',
    'Resort_Beach_SkinnyDip': 'Resort_Beach',
    'Resort_NightLounge':  'Resort_NightLounge',
    'Resort_NightLounge_Enter': 'Resort_NightLounge',
    'Resort_Yacht':        'Resort_Yacht',
    'Resort_Overnight':    'Resort',
    // BGM values (lowercase) — used as fallback location hint from #bgm: tag
    'apartment':  Location.HOME,
    'outside':    'Outside',
    'bar':        Location.BAR,
    'beach':      Location.BEACH,
    'club':       Location.CLUB,
    'mall':       Location.MALL,
    'salon':      Location.SALON,
    'park':       Location.PARK,
    'gym':        Location.GYM,    // Gym uses #bgm:outside, but GymHub sets path correctly
    'clinic':     Location.CLINIC,
    'spa':        'Spa',
    'chill':      Location.TATTOO,
};

// ── Keyword matcher ────────────────────────────────────────────────────
// Used by ACTIVITY_BADGE_MAPPINGS only (v0.2.7+). Single-word keys use
// word-boundary matching so e.g. 'shower' doesn't substring-match a tag
// like 'showering'. Multi-word keys keep simple substring semantics —
// collision-resistant by length and used for full-phrase matches like
// 'apply makeup' or 'practice posing'.
//
// Pre-v0.2.7 this also powered the LOCATION_DEFS hotspot matchText system
// (Phase 1 + 3 + click-time fallback + _detectLocation score-based). That
// was removed in favour of universal `#hotspot:` Ink tag coverage; the
// orphan-hotspot linter (tests/regression/locations-orphan-hotspot.spec.js)
// enforces that every tag references an existing pin id.
const RX_ESC = /[.*+?^${}()|[\]\\]/g;
const _wordReCache = new Map();
function matchesKeyword(text, kw) {
    if (typeof text !== 'string' || typeof kw !== 'string' || !kw) return false;
    if (kw.indexOf(' ') !== -1) return text.indexOf(kw) !== -1;
    let re = _wordReCache.get(kw);
    if (!re) {
        re = new RegExp(`(^|[^a-z0-9])${kw.replace(RX_ESC, '\\$&')}([^a-z0-9]|$)`, 'i');
        _wordReCache.set(kw, re);
    }
    return re.test(text);
}

// ── Hotspot definitions per location ────────────────────────────────────

const LOCATION_DEFS = {
    // ── Apartment ──────────────────────────────────────────────────────
    [Location.HOME]: {
        name: 'Apartment',
        bgImage: 'apartment_room',
        loftBgImage: 'loft_room',
        hotspots: [
            { id: 'computer',    label: 'Computer', x: 30.2,   y: 46.8, thumb: 'Apartment/apartment_computer' },
            { id: 'bedroom',     label: 'Bedroom', x: 65.2,   y: 58, thumb: 'Apartment/apartment_bedroom' },
            { id: 'couch',       label: 'Couch', x: 44.8,   y: 79.8, thumb: 'Apartment/apartment_couch_cuddle' },
            { id: 'go_outside',  label: 'Go Outside', x: 13,   y: 55.1, thumb: 'Apartment/apartment_door' },
            { id: 'wardrobe',    label: 'Wardrobe', x: 44.4,   y: 35.2, thumb: 'Apartment/apartment_mirror_masc' },
            { id: 'vanity',      label: 'Vanity', x: 57.8,   y: 28.6, thumb: 'Apartment/upgrade_vanity_1' },
            { id: 'upgrades',    label: 'Upgrades', x: 80.4,   y: 35.7, thumb: 'Apartment/apartment_bedroom_luxury' },
            { id: 'bathroom',    label: 'Bathroom', x: 72.1,   y: 30.5, thumb: 'Apartment/apartment_shower' },
            { id: 'basement',    label: 'Basement', x: 22,     y: 91, thumb: 'Apartment/basement_workshop' },
            { id: 'playroom',    label: 'Playroom', x: 83,     y: 78, thumb: 'Apartment/playroom_main' },
        ],
    },
    // ── Basement Workshop (v0.2.8) ──────────────────────────────────────
    ['Basement']: {
        name: 'Basement',
        bgImage: 'basement_workshop',
        hotspots: [
            { id: 'toys',           label: 'Toy Display',   x: 25, y: 30, thumb: 'Apartment/basement_toydisplay' },
            { id: 'bondage',        label: 'Practice Nook', x: 55, y: 30, thumb: 'Apartment/basement_bondage' },
            { id: 'journal',        label: 'Hidden Journal', x: 75, y: 30, thumb: 'Apartment/basement_journal' },
            { id: 'stocks',         label: 'Stocks',        x: 25, y: 55, thumb: 'Apartment/basement_stocks' },
            // v0.2.7 NPC invite pins — without these the invite choices fall
            // through to the extras list AND render as inline choices,
            // producing the visible duplicate-choice bug. Dedicated invite
            // preview images (anticipation/threshold beats) used as pin
            // thumbs so each invite reads as "NPC at the door" at a glance.
            { id: 'invite_master',  label: 'Invite Master',  x: 50, y: 55, thumb: 'Basement/basement_invite_master' },
            { id: 'invite_vincent', label: 'Invite Vincent', x: 75, y: 55, thumb: 'Basement/basement_invite_vincent' },
            // invite_eloise removed in v0.2.7 — pending LoRA retrain
            { id: 'exit',           label: 'Up the Stairs',  x: 8,  y: 85 },
        ],
    },
    // ── Playroom (v0.2.9) ───────────────────────────────────────────────
    ['Playroom']: {
        name: 'Playroom',
        bgImage: 'playroom_main',
        hotspots: [
            { id: 'mirror',       label: 'Mirror Wall',     x: 22, y: 35, thumb: 'Apartment/playroom_mirror' },
            { id: 'rack',         label: 'Equipment Rack',  x: 75, y: 35, thumb: 'Apartment/playroom_equipment_rack' },
            { id: 'positions',    label: 'Position Mat',    x: 50, y: 50, thumb: 'Apartment/playroom_positions' },
            { id: 'swing',        label: 'Sex Swing',       x: 80, y: 55, thumb: 'Apartment/playroom_swing' },
            // v0.2.7 NPC invite pins (same fix as Basement) — dedicated
            // preview images per NPC at the playroom threshold.
            { id: 'invite_gf',    label: 'Invite GF',       x: 25, y: 70, thumb: 'Playroom/playroom_invite_gf' },
            { id: 'invite_kai',   label: 'Invite Kai',      x: 50, y: 75, thumb: 'Playroom/playroom_invite_kai' },
            { id: 'invite_hana',  label: 'Invite Hana',     x: 75, y: 70, thumb: 'Playroom/playroom_invite_hana' },
            { id: 'exit',         label: 'Back to Apartment', x: 8,  y: 88 },
        ],
    },
    // ── Outside / Neighbourhood ──────────────────────────────────────────
    ['Outside']: {
        name: 'Neighbourhood',
        bgImage: 'map_neighbourhood_day',
        nightBgImage: 'map_neighbourhood_night',
        hotspots: [
            { id: 'apartment',    label: 'Apartment', x: 31.2, y: 36.5, thumb: 'Locations/apartment_room' },
            { id: 'bar',          label: 'Bar', x: 63.9, y: 33.6, thumb: 'Locations/bar_exterior_evening' },
            { id: 'park',         label: 'Park', x: 78.4, y: 94.5, thumb: 'Locations/park_landscape' },
            { id: 'salon',        label: 'Beauty Salon', x: 60,   y: 62.1, thumb: 'Salon/salon_interior' },
            { id: 'corner_store', label: 'Corner Store', x: 85,   y: 47.1, thumb: 'Locations/corner_store_interior' },
            { id: 'streets',      label: 'Walk Around', x: 44.1, y: 63.4, thumb: 'Locations/street_catcall_sway' },
            { id: 'bus_stop',     label: 'Bus Stop', x: 7.8,  y: 64.8 },
            { id: 'resort_bus',   label: 'Cove Resort', x: 7.8,  y: 86.2 },
        ],
    },
    // ── Downtown ─────────────────────────────────────────────────────────
    // ── Downtown ─────────────────────────────────────────────────────────
    ['Downtown']: {
        name: 'Downtown',
        bgImage: 'map_downtown_day',
        nightBgImage: 'map_downtown_night',
        hotspots: [
            { id: 'mall',      label: 'Mall', x: 50,   y: 35,   thumb: 'Locations/mall_interior' },
            { id: 'club',      label: 'Club', x: 36.3, y: 24.6, thumb: 'Locations/loc_club_interior' },
            { id: 'beach',     label: 'Beach', x: 4.3,  y: 53.2, thumb: 'Locations/beach_panorama' },
            { id: 'gym',       label: 'Gym', x: 24.4, y: 15.5, thumb: 'Locations/gym_room' },
            { id: 'office',    label: 'Office', x: 65,   y: 45,   thumb: 'Locations/cafe_meeting' },
            { id: 'university', label: 'University', x: 72, y: 24, thumb: 'University/university_exterior' },
            { id: 'construction', label: 'Construction Site', x: 78, y: 62 },
            { id: 'cleaning',  label: 'Cleaning Client', x: 14.8, y: 36.7, thumb: 'Mall/maid_outfit_display' },
            { id: 'clinic',    label: 'Clinic', x: 84.1, y: 39.5, thumb: 'Locations/clinic_interior' },
            { id: 'spa',       label: 'Spa', x: 36.9, y: 49.5, thumb: 'Locations/spa_interior' },
            { id: 'tattoo',    label: 'Tattoo Studio', x: 93.7, y: 32 },
            { id: 'red_light', label: 'Red Light District', x: 58.2, y: 12.4 },
            { id: 'bus_stop',  label: 'Bus Stop (Home)', x: 39.6, y: 78.6 },
        ],
    },
    // ── Construction Site (Baustelle) — masc-route job track #2. foundby:WXD ──
    // Hotspot coords are first-pass estimates against map_construction_day's
    // zones (gate left, office cabin left-centre, build/scaffold centre,
    // materials right, break area corner) — tune visually via the hotspot editor.
    ['Construction']: {
        name: 'Construction Site',
        bgImage: 'map_construction_day',
        hotspots: [
            { id: 'site_work',   label: 'Work a Shift',     x: 50, y: 42 },
            { id: 'site_office', label: 'Site Office',      x: 23, y: 46 },
            { id: 'materials',   label: 'Materials Yard',   x: 76, y: 52 },
            { id: 'break_room',  label: 'Break Area',       x: 86, y: 76 },
            { id: 'foreman',     label: 'Change Role',      x: 13, y: 68 },
            { id: 'leave',       label: 'Back to Downtown', x: 50, y: 92 },
        ],
    },
    // ── Red Light District ────────────────────────────────────────────
    ['RedLight']: {
        name: 'Red Light District',
        bgImage: 'map_red_light',
        hotspots: [
            { id: 'brothel',   label: 'Velvet Lounge', x: 57.3, y: 22.2 },
            { id: 'cabaret',   label: 'The Gilded Cage', x: 35.5, y: 28.4 },
            { id: 'maid_cafe', label: 'Maid Cafe', x: 8.6,  y: 77.1 },
            { id: 'fetish',    label: 'Kitten & Chrome', x: 82.5, y: 52.8 },
            { id: 'look',      label: 'Look Around', x: 64.3, y: 66.5 },
            { id: 'leave',     label: 'Back to Downtown', x: 47,   y: 91.1 },
        ],
    },
    // ── Velvet Lounge (Brothel) ──────────────────────────────────────
    ['Brothel']: {
        name: 'Velvet Lounge',
        bgImage: 'velvet_lounge_interior',
        hotspots: [
            { id: 'reception',    label: 'Reception', x: 50,  y: 45 },
            { id: 'stage',        label: 'Stage', x: 25,  y: 30 },
            { id: 'bar',          label: 'Bar', x: 75,  y: 35 },
            { id: 'private',      label: 'Private Rooms', x: 15,  y: 65 },
            { id: 'dressing',     label: 'Dressing Room', x: 85,  y: 70 },
            { id: 'leave',        label: 'Leave', x: 50,  y: 92 },
        ],
    },
    // ── The Gilded Cage (Cabaret, 0.3) ───────────────────────────────
    [Location.CABARET]: {
        name: 'The Gilded Cage',
        bgImage: 'cabaret_interior',
        hotspots: [
            { id: 'stage',    label: 'Stage', x: 50,  y: 30 },
            { id: 'floor',    label: 'The Floor', x: 30,  y: 58 },
            { id: 'bar',      label: 'Bar', x: 78,  y: 48 },
            { id: 'dressing', label: 'Dressing Room', x: 14,  y: 36 },
            { id: 'office',   label: 'Maxime', x: 88,  y: 26 },
            { id: 'leave',    label: 'Leave', x: 50,  y: 92 },
        ],
    },
    // ── Maid Cafe ────────────────────────────────────────────────────
    ['MaidCafe']: {
        name: 'Maid Cafe',
        bgImage: 'maid_cafe_interior',
        hotspots: [
            { id: 'counter',      label: 'Counter', x: 58.3, y: 53 },
            { id: 'tables',       label: 'Tables', x: 21.3, y: 62.9 },
            { id: 'stage',        label: 'Stage', x: 80.3, y: 61.3 },
            { id: 'kitchen',      label: 'Kitchen', x: 70.3, y: 39.1 },
            { id: 'dressing',     label: 'Dressing Room', x: 62.6, y: 26.5 },
            { id: 'leave',        label: 'Leave', x: 30.1, y: 92.7 },
        ],
    },
    // ── Cove Resort (v0.2.5) ─────────────────────────────────────────
    ['Resort']: {
        name: 'Cove Resort',
        bgImage: 'resort_overview',
        duskBgImage: 'resort_overview_dusk',
        nightBgImage: 'resort_overview_night',
        hotspots: [
            { id: 'lobby',       label: 'Lobby', x: 14, y: 40, thumb: 'Resort/resort_lobby' },
            { id: 'pool',        label: 'Pool Deck', x: 37, y: 46, thumb: 'Resort/resort_pool_deck' },
            { id: 'tiki',        label: 'Tiki Bar', x: 50, y: 25, thumb: 'Resort/resort_tiki_bar' },
            { id: 'cabana',      label: 'Cabana Row', x: 83, y: 58, thumb: 'Resort/resort_cabana' },
            { id: 'beach',       label: 'Beach Strip', x: 45, y: 78, thumb: 'Resort/resort_beach_dusk' },
            { id: 'nightlounge', label: 'Night Lounge', x: 80, y: 18, thumb: 'Resort/resort_night_lounge' },
            { id: 'yacht',       label: 'Yacht', x: 92, y: 78, thumb: 'Resort/resort_yacht_day' },
        ],
    },
    ['Resort_Cabana']: {
        name: 'Cabana Row',
        bgImage: 'resort_cabana',
        nightBgImage: 'resort_cabana_night',
        hotspots: [
            { id: 'sleep',  label: 'Retire', x: 60, y: 35, thumb: 'Resort/resort_cabana_night' },
            { id: 'rent',   label: 'Rent a private cabana', x: 30, y: 55, thumb: 'Resort/resort_cabana_couple' },
            { id: 'shift',  label: 'Cabana shift', x: 78, y: 60, thumb: 'Resort/resort_iris_uniform_reveal' },
            { id: 'leave',  label: 'Grounds', x: 15, y: 92 },
        ],
    },
    ['Resort_Beach']: {
        name: 'Beach Strip',
        bgImage: 'resort_beach_dusk',
        nightBgImage: 'resort_beach_night',
        hotspots: [
            { id: 'walk',       label: 'Walk', x: 35, y: 60, thumb: 'Resort/resort_beach_dusk' },
            { id: 'sunset',     label: 'Sit for sunset', x: 60, y: 45, thumb: 'Resort/resort_beach_sunset_solo' },
            { id: 'skinnydip',  label: 'Skinny dip', x: 75, y: 70, thumb: 'Resort/resort_beach_skinnydip' },
            { id: 'dante',      label: 'Dante', x: 55, y: 78, thumb: 'Resort/resort_dante_beach_walk' },
            { id: 'leave',      label: 'Grounds', x: 15, y: 92 },
        ],
    },
    ['Resort_NightLounge']: {
        name: 'Night Lounge',
        bgImage: 'resort_night_lounge',
        nightBgImage: 'resort_night_lounge',
        hotspots: [
            { id: 'enter',  label: 'Step in', x: 50, y: 45, thumb: 'Resort/resort_night_lounge' },
            { id: 'dante',  label: 'Dante', x: 75, y: 52, thumb: 'Resort/resort_dante_booth' },
            { id: 'leave',  label: 'Grounds', x: 15, y: 92 },
        ],
    },
    ['Resort_Yacht']: {
        name: 'Yacht',
        bgImage: 'resort_yacht_day',
        duskBgImage: 'resort_yacht_dusk',
        nightBgImage: 'resort_yacht_night',
        hotspots: [
            { id: 'tour',  label: 'Tour continues', x: 50, y: 45, thumb: 'Resort/resort_yacht_day' },
            { id: 'deck',  label: 'On deck', x: 60, y: 55, thumb: 'Resort/resort_yacht_dusk' },
            { id: 'leave', label: 'Grounds', x: 15, y: 92 },
        ],
    },
    ['Resort_Lobby']: {
        name: 'Resort Lobby',
        bgImage: 'resort_lobby',
        nightBgImage: 'resort_lobby_night',
        hotspots: [
            { id: 'concierge', label: 'Concierge', x: 50, y: 42 },
            { id: 'boutique',  label: 'Boutique', x: 78, y: 60 },
            { id: 'dante',     label: 'Someone at the bar', x: 22, y: 55 },
            { id: 'cabana',    label: 'Retire', x: 65, y: 35 },
            { id: 'checkout',  label: 'Check Out', x: 10, y: 78 },
            { id: 'leave',     label: 'Grounds', x: 50, y: 92 },
        ],
    },
    ['Resort_PoolDeck']: {
        name: 'Pool Deck',
        bgImage: 'resort_pool_deck',
        nightBgImage: 'resort_pool_deck_night',
        hotspots: [
            { id: 'sunbathe', label: 'Sunbathe', x: 50, y: 55 },
            { id: 'drink',    label: 'Drink', x: 75, y: 65 },
            { id: 'leave',    label: 'Grounds', x: 15, y: 92 },
        ],
    },
    ['Resort_TikiBar']: {
        name: 'Tiki Bar',
        bgImage: 'resort_tiki_bar',
        nightBgImage: 'resort_tiki_bar_night',
        hotspots: [
            { id: 'iris',   label: 'Iris', x: 50, y: 40 },
            { id: 'shift',  label: 'Work', x: 70, y: 55 },
            { id: 'drink',  label: 'Drink', x: 30, y: 60 },
            { id: 'dante',  label: 'Dante', x: 82, y: 42, thumb: 'Resort/resort_dante_tiki_invite' },
            { id: 'leave',  label: 'Grounds', x: 15, y: 92 },
        ],
    },
    // ── Spa ──────────────────────────────────────────────────────────
    ['Spa']: {
        name: 'Spa',
        bgImage: 'spa_interior',
        hotspots: [
            { id: 'massage',  label: 'Massage', x: 23.8, y: 49.3, thumb: 'Spa/spa_overview' },
            { id: 'facial',   label: 'Facial', x: 41.7, y: 67.1, thumb: 'Spa/spa_facial' },
            { id: 'steam',    label: 'Steam Room', x: 69.8, y: 45.3, thumb: 'Spa/spa_steam_event_compliment' },
            { id: 'lounge',   label: 'Lounge', x: 61.9, y: 76.8, thumb: 'Spa/spa_relax' },
            { id: 'body',     label: 'Body Treatments', x: 44.4, y: 34.6, thumb: 'Spa/spa_body_lips' },
            { id: 'dominic',  label: 'Dominic', x: 14.8, y: 56,   thumb: 'Spa/spa_dominic_intro' },
            { id: 'camille',  label: 'Camille', x: 83.4, y: 55.7, thumb: 'Spa/spa_camille_intro' },
            { id: 'leave',    label: 'Leave', x: 36.6, y: 83.9 },
        ],
    },
    // ── Corner Store ─────────────────────────────────────────────────
    ['CornerStore']: {
        name: 'Corner Store',
        bgImage: 'corner_store_interior',
        hotspots: [
            { id: 'shelves',   label: 'Shelves', x: 71.2, y: 74.3 },
            { id: 'pharmacy',  label: 'Pharmacy', x: 56.3, y: 31.3 },
            { id: 'snacks',    label: 'Snacks', x: 19.8, y: 78.4 },
            { id: 'leave',     label: 'Leave', x: 47.4, y: 96.2 },
        ],
    },
    // ── Bar ───────────────────────────────────────────────────────────
    [Location.BAR]: {
        name: 'Bar',
        bgImage: 'bar_interior',
        hotspots: [
            { id: 'work',         label: 'Work', x: 35,   y: 34.5, thumb: 'Bar/bar_serving_polo' },
            { id: 'outfit',       label: 'Change Outfit', x: 37,   y: 86.5, thumb: 'Bar/bar_first_inspection' },
            { id: 'work_storage', label: 'Stockroom', x: 19.8,   y: 93.8, thumb: 'Bar/bar_back_alley' },
            { id: 'wait',         label: 'Wait Here', x: 87.8,   y: 40.9, thumb: 'Locations/bar_interior' },
            { id: 'leave',        label: 'Leave', x: 47,   y: 91.1, thumb: 'Locations/bar_exterior_evening' },
        ],
    },
    // ── Park ──────────────────────────────────────────────────────────
    [Location.PARK]: {
        name: 'Park',
        bgImage: 'park_map',
        hotspots: [
            { id: 'walk',          label: 'Go for a Walk', x: 37.4,   y: 56.3, thumb: 'Park/park_bench_sitting' },
            { id: 'toilets',       label: 'Restrooms', x: 78.0,   y: 82.0, thumb: 'Locations/men_stall' },
            { id: 'photographer',  label: 'Photographer', x: 71.5,   y: 26.5, thumb: 'Park/park_photographer' },
            { id: 'secluded',      label: 'Secluded Spot', x: 13,   y: 56.6, thumb: 'Park/park_bushes_hidden' },
            { id: 'pose',          label: 'Strike a Pose', x: 55.7,   y: 28.7, thumb: 'Locations/park_overview' },
            { id: 'leave',         label: 'Leave', x: 47,   y: 91.1, thumb: 'Locations/park_landscape' },
        ],
    },
    // Salon: no interactive map — regular choices
    // ── Beach ──────────────────────────────────────────────────────────
    [Location.BEACH]: {
        name: 'Beach',
        bgImage: 'beach_panorama',
        hotspots: [
            { id: 'sunbathe',   label: 'Sunbathe', x: 48,   y: 55, thumb: 'Beach/beach_sunbathe' },
            { id: 'swim',       label: 'Swim', x: 34,   y: 14, thumb: 'Beach/beach_swim' },
            { id: 'cold_dip',   label: 'Cold Dip', x: 40,   y: 36, thumb: 'Beach/beach_swim' },
            { id: 'walk_shore', label: 'Walk the Shore', x: 71,   y: 34, thumb: 'Beach/beach_shore_walk' },
            { id: 'lifeguard',  label: 'Lifeguard Tower', x: 17,   y: 56, thumb: 'Beach/beach_overview' },
            { id: 'far_end',    label: 'Far End', x: 60,   y: 7,  thumb: 'Beach/beach_behind_dunes' },
            { id: 'leave',      label: 'Leave', x: 47,   y: 91.1, thumb: 'Locations/beach_panorama' },
        ],
    },
    // ── Gym ────────────────────────────────────────────────────────────
    [Location.GYM]: {
        name: 'Gym',
        bgImage: 'gym_room',
        hotspots: [
            { id: 'weights',  label: 'Weights', x: 61,   y: 58, thumb: 'Gym/gym_weights' },
            { id: 'cardio',   label: 'Cardio', x: 49,   y: 79, thumb: 'Gym/gym_cardio' },
            { id: 'boxing',   label: 'Boxing', x: 21,   y: 71, thumb: 'Gym/gym_boxing' },
            { id: 'yoga',     label: 'Yoga', x: 29,   y: 35, thumb: 'Gym/gym_yoga' },
            { id: 'sauna',    label: 'Sauna', x: 71,   y: 27, thumb: 'Gym/gym_sauna' },
            { id: 'locker',   label: 'Locker Room', x: 79,   y: 80, thumb: 'Gym/gym_locker' },
            { id: 'malik',    label: 'Trainer', x: 44,   y: 56, thumb: 'Gym/gym_trainer_intro' },
            // Sissy Body Training (0.2.9; freed + kill-switch stripped 2026-07-07).
            // Pin is always defined; the tile renders when the
            // #hotspot:tyler_coach choice is present. Thumb reuses
            // the existing gym_locker_tyler (Tyler) until gym_tyler_coach_intro renders.
            { id: 'tyler_coach', label: 'Body Coach', x: 34, y: 46, thumb: 'Gym/gym_locker_tyler' },
            { id: 'shake',    label: 'Protein Shake', x: 63,   y: 84, thumb: 'Gym/gym_overview' },
            { id: 'leave',    label: 'Leave', x: 47,   y: 91.1, thumb: 'Locations/gym_room' },
        ],
    },
    // ── Clinic ─────────────────────────────────────────────────────────
    [Location.CLINIC]: {
        name: 'Clinic',
        bgImage: 'clinic_interior',
        hotspots: [
            { id: 'reception', label: 'Reception', x: 53,   y: 42, thumb: 'Clinic/clinic_reception' },
            { id: 'doctor',    label: "Doctor's Office", x: 25,   y: 40, thumb: 'Clinic/clinic_doctor_intro' },
            { id: 'treatment', label: 'Treatment Room', x: 15,   y: 54, thumb: 'Clinic/clinic_treatment' },
            { id: 'prostate',  label: 'Prostate Therapy', x: 84,   y: 50, thumb: 'Clinic/clinic_prostate_prep' },
            { id: 'lab',       label: 'Lab', x: 52,   y: 23, thumb: 'Clinic/clinic_lab' },
            { id: 'pharmacy',  label: 'Pharmacy', x: 91.9,   y: 36.4, thumb: 'Clinic/clinic_pharmacy' },
            { id: 'leave',     label: 'Leave', x: 47,   y: 91.1, thumb: 'Locations/clinic_interior' },
        ],
    },
    // ── Club ───────────────────────────────────────────────────────────
    [Location.CLUB]: {
        name: 'Club',
        bgImage: 'loc_club_interior',
        hotspots: [
            { id: 'dance',   label: 'Dance Floor', x: 47.6,   y: 56.6, thumb: 'Club/club_dance_close' },
            { id: 'toilets', label: 'Toilets', x: 20.7,   y: 71.7, thumb: 'Locations/men_stall' },
            { id: 'vip',     label: 'VIP Room', x: 45.1,   y: 16.3, thumb: 'Club/club_vip_lounge' },
            { id: 'club_bar', label: 'Bar', x: 29.6, y: 45.8, thumb: 'Club/club_bar' },
            { id: 'work',    label: 'Work', x: 35,   y: 34.5, thumb: 'Club/club_work_shotgirl' },
            { id: 'relief',  label: 'Relief', x: 12.1,   y: 66.3, thumb: 'Locations/men_stall' },
            { id: 'leave',   label: 'Leave', x: 47,   y: 91.1,   thumb: 'Club/club_exterior' },
        ],
    },
    // ── Mall ───────────────────────────────────────────────────────────
    [Location.MALL]: {
        name: 'Mall',
        bgImage: 'mall_interior',
        hotspots: [
            { id: 'mens',       label: "Men's Fashion", x: 31.6,   y: 69.9, thumb: 'Mall/mall_mens_store' },
            { id: 'womens',     label: "Women's Clothing", x: 34.5,   y: 34.3, thumb: 'Mall/mall_womens_section' },
            { id: 'adultstore', label: 'Adult Store', x: 9.2,   y: 39.7, thumb: 'Mall/mall_adult_store' },
            { id: 'pharmacy',   label: 'Pharmacy', x: 91.9,   y: 36.4, thumb: 'Mall/mall_pharmacy' },
            { id: 'foodcourt',  label: 'Food Court', x: 76.3,   y: 31.5, thumb: 'Mall/mall_food_court' },
            { id: 'bathroom',   label: 'Bathroom', x: 53.4,   y: 81.2, thumb: 'Locations/men_stall' },
            { id: 'lingerie',   label: 'Lingerie', x: 12.1,   y: 87.3, thumb: 'Mall/mall_lingerie_boutique' },
            { id: 'leave',      label: 'Leave', x: 47,   y: 91.1,   thumb: 'Mall/mall_entrance' },
        ],
    },
    // ── Spa (map disabled — using normal choices with thumbnails) ────
    // [Location.SPA]: {
    //     name: 'Spa',
    //     bgImage: 'spa_interior',
    //     hotspots: [
    //         { id: 'massage',         label: 'Massage', x: 23.9, y: 48.5, thumb: 'Spa/spa_overview' },
    //         { id: 'facial',          label: 'Facial', x: 41.2, y: 32.3, thumb: 'Spa/spa_facial' },
    //         { id: 'steam',           label: 'Steam Room', x: 71.7, y: 43.6, thumb: 'Spa/spa_steam_event_compliment' },
    //         { id: 'relax',           label: 'Relaxation', x: 84.1, y: 54.8, thumb: 'Spa/spa_relax' },
    //         { id: 'body',            label: 'Body Treatments', x: 43.4, y: 66.6, thumb: 'Spa/spa_body_lips' },
    //         { id: 'dominic',         label: 'Dominic', x: 52.8, y: 37.2, thumb: 'Spa/spa_dominic_intro' },
    //         { id: 'camille',         label: 'Camille', x: 30.0, y: 55.0, thumb: 'Spa/spa_camille_intro' },
    //         { id: 'tea',             label: 'Herbal Tea', x: 63.1, y: 76.2, thumb: 'Spa/spa_camille_tea' },
    //         { id: 'leave',           label: 'Leave', x: 47,   y: 91.1, thumb: 'Locations/spa_interior' },
    //     ],
    // },
    [Location.TATTOO]: {
        name: 'Tattoo Studio',
        bgImage: 'tattoo_studio',
        hotspots: [
            { id: 'browse',          label: 'Browse Tattoos', x: 75,   y: 35, thumb: 'Tattoo/tattoo_rose' },
            { id: 'jade',            label: 'Talk to Jade', x: 45,   y: 50, thumb: 'Locations/tattoo_studio' },
            { id: 'leave',           label: 'Leave', x: 47,   y: 91.1, thumb: 'Locations/tattoo_studio' },
        ],
    },
};

// ── Activity badge mappings: choice text substring → hotspot id ────────────
// Unmatched choices containing these strings get attached as extra activities
// on the named hotspot (mirroring Unity's ActivityBadgeMappings).
const ACTIVITY_BADGE_MAPPINGS = {
    [Location.HOME]: [
        { textContains: 'shower',            hotspotId: 'bathroom' },
        { textContains: 'apply makeup',      hotspotId: 'vanity' },
        { textContains: 'nail touch',        hotspotId: 'vanity' },
        { textContains: 'hair touch',        hotspotId: 'vanity' },
        { textContains: 'practice posing',   hotspotId: 'vanity' },
        { textContains: 'journal',           hotspotId: 'bedroom' },
        { textContains: 'anal training',     hotspotId: 'bedroom' },
        { textContains: 'dildo',             hotspotId: 'bedroom' },
        { textContains: 'vibrator',          hotspotId: 'bedroom' },
        { textContains: 'deep breath',       hotspotId: 'couch' },
        { textContains: 'breathe',           hotspotId: 'couch' },
        { textContains: 'enough anymore',    hotspotId: 'bedroom' },
        { textContains: 'something bigger',  hotspotId: 'bedroom' },
        { textContains: 'barely registers',  hotspotId: 'bedroom' },
        { textContains: 'bigger plug',       hotspotId: 'bedroom' },
    ],
    // [Location.SPA]: [
    //     { textContains: 'deep tissue',        hotspotId: 'massage' },
    //     { textContains: 'lip enhancement',    hotspotId: 'body' },
    //     { textContains: 'hip sculpting',      hotspotId: 'body' },
    //     { textContains: 'breast enhancement', hotspotId: 'body' },
    // ],
};

// NPC portrait configuration (mirrors Unity's NpcPortraitConfig)
const NPC_PORTRAIT_CONFIG = {
    master:     { portrait: 'master_casual',     x: 1,  y: 68, cssClass: 'location-npc--master' },
    girlfriend: { portrait: 'girlfriend_casual', x: 1,  y: 82, cssClass: 'location-npc--girlfriend' },
    // y:40 (was 54) — y:54 collided with the Apartment/Loft 'Go Outside'
    // hotspot at (x:13, y:55.1) on 1920x1080 standalone. foundby:The Cook
    // v0.2.6.1. The weekend pill now sits in the upper-left zone with no
    // hotspots nearby; Master/GF stack below at 68/82 unchanged.
    weekend:    { portrait: 'iris_casual',       x: 1,  y: 40, cssClass: 'location-npc--weekend' },
};

// NOTE: Do NOT reintroduce a JS-side hotspot→location shortcut map here.
// Ink's `-> Knot` divert (resolved via storyManager.selectChoice) is the
// single source of truth. A previous HOTSPOT_LOCATION_MAP duplicated those
// diverts in JS and drifted out of sync every time a new sub-location
// shipped — caused 3+ regressions (Resort v0.2.5.1, Mobile-Resort v0.2.5.4,
// RedLight v0.2.5.8.1) before being removed in v0.2.6. The async-refresh
// race that originally motivated the shortcut was fixed in story.js
// (Generation counter + isProcessing reset, commit 14f67641, March 2026)
// and selectChoice has been the only path for Resort sub-locations since
// v0.2.5.1 with zero reported issues. Linter:
// `tools/hotspot_shortcut_linter.py` will fail if this map is reintroduced.

class LocationsUI {
    constructor() {
        /** @type {HTMLElement|null} */
        this._root = null;

        /** @type {string|null} Current location key */
        this._activeLocation = null;

        /** @type {boolean} Is using loft apartment layout */
        this._isLoft = false;

        /** @type {boolean} Is in fullscreen (phone) mode */
        this._isFullscreen = false;

        /** @type {Map<string, number>} Hotspot id -> primary choice index */
        this._hotspotChoiceMap = new Map();

        /** @type {Map<string, Array<{index:number, text:string, choice:object}>>} Hotspot id -> activity choices */
        this._hotspotActivities = new Map();

        /** @type {HTMLElement|null} Active popover element */
        this._activePopover = null;

        /** @type {Function|null} Story accessor */
        this._storyRef = null;

        /** @type {boolean} Quick travel mode active (from #quicktravelpossible tag) */
        this._quickTravelActive = false;

        /** @type {Array<{element:HTMLElement, choiceIdx:number, hasActivities:boolean}>} Ordered interactable hotspots for keyboard nav */
        this._orderedHotspots = [];

        /** @type {Function|null} Bound keydown handler for cleanup */
        this._boundKeyHandler = null;

        this._scope = events.createScope('locations');
        this._bindEvents();
    }

    destroy() {
        if (this._scope) {
            this._scope.destroy();
            this._scope = null;
        }
    }

    /** Set story accessor for NPC portrait badges. */
    setStoryAccessor(fn) { this._storyRef = fn; }
    setStoryManager(sm) { this._storyManager = sm; }

    // ── Public API ──────────────────────────────────────────────────────

    init() {
        // Location view renders INSIDE the story content area (like Unity),
        // not as a separate overlay. It sits above the story text.
        this._root = document.getElementById('location-view');
        if (!this._root) {
            this._root = this._createContainer();
        }
    }

    /**
     * Try to match current choices to a location's hotspots.
     * If matched, renders the interactive location view.
     * Returns true if location view was activated, false otherwise.
     * @param {Array<{text: string, index: number, tags: string[]}>} choices
     * @param {string} locationHint - Optional location hint from Ink variable
     * @returns {boolean}
     */
    tryActivate(choices, locationHint) {
        const locationKey = this._detectLocation(choices, locationHint);
        if (!locationKey) {
            this._deactivate();
            return false;
        }

        const def = LOCATION_DEFS[locationKey];
        if (!def) {
            this._deactivate();
            return false;
        }

        this._activeLocation = locationKey;
        this._currentLocDef = def;
        this._matchChoicesToHotspots(choices, def);

        // Show map when we have enough matched hotspots (at least 2)
        // Entry screens (e.g. GymEntry with only "Leave") don't show maps
        if (this._hotspotChoiceMap.size >= 2) {
            this._lastChoices = choices;
            this._render(def, choices);
            // Verify render succeeded — if root is empty, fall back to text choices
            if (this._root && this._root.children.length === 0) {
                this._deactivate();
                return false;
            }
            return true;
        }

        this._deactivate();
        return false;
    }

    /** Deactivate location view. */
    deactivate() {
        this._deactivate();
    }

    /** Set whether we're in loft mode (moved_out == true). */
    setLoftMode(isLoft) {
        this._isLoft = isLoft;
    }

    // ── Event Bindings ──────────────────────────────────────────────────

    _bindEvents() {
        this._scope.on(Events.VARIABLE_CHANGED, (name, value) => {
            if (name === 'moved_out') {
                this._isLoft = !!value;
            }
            if (name === 'quickTravelAllowed') {
                this._quickTravelActive = !!value;
            }
        });

        // Re-render location hotspot labels when language changes
        this._scope.on(Events.LOCALE_CHANGED, () => {
            if (this._activeLocation && this._currentLocDef && this._lastChoices) {
                this._matchChoicesToHotspots(this._lastChoices, this._currentLocDef);
                this._render(this._currentLocDef, this._lastChoices);
            }
        });
    }

    // ── Keyboard Navigation ──────────────────────────────────────────────

    /**
     * Handle keypress for number-key hotspot navigation.
     * Keys 1-9 click the corresponding hotspot in display order.
     * @param {KeyboardEvent} e
     */
    _handleKeyPress(e) {
        // Ignore if typing in an input field
        if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;

        // Escape closes active popover
        if (e.key === 'Escape' && this._activePopover) {
            this._closePopover();
            e.preventDefault();
            return;
        }

        const num = parseInt(e.key, 10);
        if (isNaN(num) || num < 1 || num > 9) return;

        // If popover/bottom-sheet is open, select its Nth option instead
        if (this._activePopover) {
            const options = this._activePopover.querySelectorAll('.location-popover-item:not(.location-popover-item--disabled)');
            const idx = num - 1;
            if (idx < options.length) {
                e.preventDefault();
                options[idx].click();
            }
            return;
        }

        // Find the hotspot button that has this number displayed
        const btn = this._root?.querySelector(`.location-hotspot[data-hotkey="${num}"]`);
        if (!btn) return;

        e.preventDefault();
        btn.click();
    }

    _bindKeyboard() {
        this._unbindKeyboard();
        this._boundKeyHandler = (e) => this._handleKeyPress(e);
        document.addEventListener('keydown', this._boundKeyHandler);
    }

    _unbindKeyboard() {
        if (this._boundKeyHandler) {
            document.removeEventListener('keydown', this._boundKeyHandler);
            this._boundKeyHandler = null;
        }
    }

    // ── Location Detection ──────────────────────────────────────────────

    _detectLocation(choices, locationHint) {
        // Primary: resolve from Ink knot name (most reliable)
        if (locationHint) {
            // Direct Location key match (e.g. "Home", "Bar")
            if (LOCATION_DEFS[locationHint]) return locationHint;
            // Ink knot name match (e.g. "Apartment", "BarEntry")
            const mapped = KNOT_TO_LOCATION[locationHint];
            if (mapped && LOCATION_DEFS[mapped]) return mapped;
            // Partial match: strip "Entry" suffix
            const base = locationHint.replace(/Entry$/, '');
            const baseMapped = KNOT_TO_LOCATION[base];
            if (baseMapped && LOCATION_DEFS[baseMapped]) return baseMapped;
            // Known location WITHOUT a map (Salon, Office, Maid) → no map
            if (mapped || baseMapped || KNOT_TO_LOCATION[locationHint]) return null;
            // locationHint is set but unrecognized → we're in a sub-scene (e.g.
            // Clerk_Testing_Scene, Dominic_Massage). Do NOT fall through to
            // score-based detection which would incorrectly match "Leave" → Mall map.
            return null;
        }

        // Score-based fallback removed v0.2.7 — relied on hotspot.matchText
        // substring matching which is no longer authoritative. If
        // locationHint doesn't resolve to a known knot, no map renders.
        return null;
    }

    _matchChoicesToHotspots(choices, def) {
        this._hotspotChoiceMap.clear();
        this._hotspotActivities.clear();
        this._hotspotDisabled = new Map(); // hotspotId -> { reason: string }

        const matchedIndices = new Set();
        const hotspotIds = new Set(def.hotspots.map(h => h.id));

        // Phase 0: explicit #hotspot: tag — highest priority, no text guessing
        for (const choice of choices) {
            const hsId = choice.hotspot;
            if (!hsId || !hotspotIds.has(hsId)) continue;
            // Track disabled choices — still add as activities so the popover shows
            if (!choice.isEnabled) {
                if (!this._hotspotDisabled.has(hsId)) {
                    this._hotspotDisabled.set(hsId, { reason: choice.disabledReason || '' });
                }
                // Add disabled choices as activities so popover appears
                if (!this._hotspotActivities.has(hsId)) this._hotspotActivities.set(hsId, []);
                this._hotspotActivities.get(hsId).push(choice);
                matchedIndices.add(choice.index);
                continue;
            }
            if (!this._hotspotChoiceMap.has(hsId)) {
                this._hotspotChoiceMap.set(hsId, choice.index);
            } else {
                if (!this._hotspotActivities.has(hsId)) this._hotspotActivities.set(hsId, []);
                this._hotspotActivities.get(hsId).push(choice);
            }
            matchedIndices.add(choice.index);
        }

        // Phase 1 + Phase 3 (matchText fallback) removed v0.2.7 — every choice
        // intended to render on a map pin now carries an explicit `#hotspot:X`
        // Ink tag. The orphan-hotspot linter (tests/regression/locations-
        // orphan-hotspot.spec.js) enforces that every `#hotspot:X` references
        // an existing pin id in the location's LOCATION_DEFS. Choices without
        // a tag fall through to the extra-choices area below the map (by
        // design — sub-menu items, contextual SMS prompts, Continue beats).
        //
        // The removed matchText logic introduced a substring-collision class
        // (BUG_PATTERNS § 19) where short single-word keywords like 'bar' or
        // 'out' silently bound to unrelated choices. Phase 0 + Phase 2 are
        // sufficient with universal #hotspot: tag coverage.

        // Phase 2: activity badge mappings — incidental choices (e.g. "Take
        // a shower" inside Apartment) attach to existing pins as badges.
        // This is a separate system from matchText: uses textContains keys
        // from ACTIVITY_BADGE_MAPPINGS, NOT from hotspot.matchText.
        const mappings = ACTIVITY_BADGE_MAPPINGS[this._activeLocation] || [];
        for (const choice of choices) {
            if (matchedIndices.has(choice.index)) continue;
            const text = (choice.originalText || choice.text).toLowerCase();
            for (const mapping of mappings) {
                if (matchesKeyword(text, mapping.textContains)) {
                    if (!this._hotspotActivities.has(mapping.hotspotId)) {
                        this._hotspotActivities.set(mapping.hotspotId, []);
                    }
                    this._hotspotActivities.get(mapping.hotspotId).push(choice);
                    matchedIndices.add(choice.index);
                    break;
                }
            }
        }
    }

    // ── Rendering ───────────────────────────────────────────────────────

    _render(def, choices) {
        if (!this._root) this.init();
        this._root.classList.remove('hidden');
        this._root.innerHTML = '';

        const storyContent = document.getElementById('story-content');
        const savedContent = storyContent ? storyContent.innerHTML : '';
        try {

        // Pre-fix (Unity-port behaviour): innerHTML = '' here unconditionally
        // wiped the story panel on every map render, even for within-family
        // transitions (MorningRoutine -> Apartment) where the player should
        // be able to scroll back to read morning narration that just played.
        // story.js's family-aware clear (KNOT_FAMILIES + _locationFamily)
        // already handles cross-family clears via STORY_CLEAR. Trusting the
        // engine to drive the clear means within-family transitions preserve
        // scrollback. Foundby:F95 anon 2026-05-19.
        //
        // 2026-05-21 follow-up (foundby:VPS player 0.2.6.6 Day 104): also
        // hide #choices-inline (the inline scroll-append container). The
        // legacy #choices div is an empty fallback in the current build;
        // hiding only it leaves the actual choice buttons visible BELOW the
        // map. main.js already covers this in the STORY_CHOICES_READY
        // listener — duplicating here is defense in depth for any callers
        // that bypass the event (LOCALE_CHANGED re-render, etc.).
        const choicesEl = document.getElementById('choices');
        if (choicesEl) choicesEl.classList.add('hidden');
        const choicesInline = document.getElementById('choices-inline');
        if (choicesInline) choicesInline.style.display = 'none';

        // Mobile: use list navigation instead of positioned hotspot map
        if (window.innerWidth < 768) {
            this._renderMobileList(def, choices);
            return;
        }

        const container = document.createElement('div');
        container.className = 'location-container';
        container.dataset.location = this._activeLocation;

        // Background image: FULLY VISIBLE (Unity style — no dimming)
        // Night variant (TimeSlot >= 3) if the location defines one
        const isNight = (() => { try { return this._storyManager?.currentStory?.variablesState['TimeSlot'] >= 3; } catch(e) { return false; } })();
        const bgImage = (this._activeLocation === Location.HOME && this._isLoft)
            ? def.loftBgImage
            : (isNight && def.nightBgImage) ? def.nightBgImage : def.bgImage;

        const bg = document.createElement('div');
        bg.className = 'location-bg';
        if (isTarMode()) {
            const blobUrl = getImageUrl(bgImage);
            if (blobUrl) bg.style.backgroundImage = `url(${blobUrl})`;
        } else {
            const folder = (window._imageManifest && window._imageManifest[bgImage]) || 'Locations';
            bg.style.backgroundImage = `url(assets/images/${folder}/${bgImage}.png)`;
        }
        container.appendChild(bg);

        // Location name (top-left, with text-shadow for readability)
        const nameEl = document.createElement('div');
        nameEl.className = 'location-name';
        const titleKey = def.name.toLowerCase() + '.title';
        const titleTranslated = L.get(titleKey);
        nameEl.textContent = (titleTranslated !== titleKey) ? titleTranslated : def.name;
        container.appendChild(nameEl);

        // Collect all matched choice indices (primary + activities + disabled) for unmatched detection
        const allMatchedIndices = new Set();
        for (const [, idx] of this._hotspotChoiceMap) allMatchedIndices.add(idx);
        for (const [, acts] of this._hotspotActivities) {
            for (const a of acts) allMatchedIndices.add(a.index);
        }
        // Disabled choices with #hotspot: tags are already on the map — don't show as extra text
        for (const choice of choices) {
            if (choice.hotspot && this._hotspotDisabled.has(choice.hotspot)) {
                allMatchedIndices.add(choice.index);
            }
        }

        // ── Apartment NPC portrait detection ──────────────────────────────
        // Identify NPC-related choices (Master/Girlfriend) for standalone portraits
        const npcMasterEvents = [];
        const npcGirlfriendEvents = [];
        const npcWeekendEvents = [];
        let masterName = null;
        let girlfriendName = null;

        if (this._activeLocation === Location.HOME) {
            try {
                const story = this._storyRef?.();
                if (story) {
                    masterName = story.variablesState['MasterName'] || null;
                    girlfriendName = story.variablesState['GirlfriendName'] || 'Sarah';
                }
            } catch(e) {}

            // Scan unmatched choices for NPC name references
            for (const choice of choices) {
                if (allMatchedIndices.has(choice.index)) continue;
                const text = choice.text || '';
                const isWeekend = text.startsWith('Weekend plans');
                const hasMaster = masterName && text.includes(masterName);
                const hasGf = girlfriendName && text.includes(girlfriendName);

                if (isWeekend) {
                    // Weekend event invite — bottom-left portrait slot above Master
                    npcWeekendEvents.push(choice);
                    allMatchedIndices.add(choice.index);
                } else if (hasMaster && hasGf) {
                    // Both names — assign to whichever appears first
                    if (text.indexOf(girlfriendName) <= text.indexOf(masterName)) {
                        npcGirlfriendEvents.push(choice);
                    } else {
                        npcMasterEvents.push(choice);
                    }
                    allMatchedIndices.add(choice.index);
                } else if (hasMaster) {
                    npcMasterEvents.push(choice);
                    allMatchedIndices.add(choice.index);
                } else if (hasGf) {
                    npcGirlfriendEvents.push(choice);
                    allMatchedIndices.add(choice.index);
                }
            }
        }

        // ── Hotspots ──────────────────────────────────────────────────────
        this._orderedHotspots = [];
        let _hotkeyCount = 0;
        for (const hotspot of def.hotspots) {
            const choiceIdx = this._hotspotChoiceMap.get(hotspot.id);
            const activities = this._hotspotActivities.get(hotspot.id) || [];
            const disabledInfo = this._hotspotDisabled.get(hotspot.id);
            const isActive = choiceIdx !== undefined;
            // Phase 0 also pushes disabled choices into _hotspotActivities so
            // the popover can surface their reason; that meant any disabled-
            // only hotspot still counted as "has activities" → interactable
            // → locked styling never applied → looked fully active. Filter
            // disabled-only activities out of the interactable check
            // (foundby v0.2.4.2 Day 20: undressed player saw a normal-looking
            // Outside pin and reported "no option to leave").
            const hasEnabledActivity = activities.some(a => a.isEnabled);
            const isDisabledChoice = !!disabledInfo;
            // A hotspot is interactable if it has a primary choice OR an
            // enabled activity (disabled-only activities don't count).
            const isInteractable = isActive || hasEnabledActivity;
            // Only show as locked if ALL choices are disabled (no enabled choice exists)
            const isFullyLocked = isDisabledChoice && !isInteractable;

            // Hide undiscovered locations: no choice AND no disabled-choice
            // means it has not been discovered yet (e.g. Tattoo/Office/Cleaning
            // before their discovery flag). Time-gated closed spots (Spa/Club/
            // Red Light) DO have a #disabled choice + stay visible with their
            // reason. Avoids spoiler "Not available right now" greyed cards.
            if (!isInteractable && !isDisabledChoice) {
                continue;
            }

            const btn = document.createElement('button');
            btn.className = 'location-hotspot' + (isInteractable ? '' : ' location-hotspot-disabled');
            if (isFullyLocked) btn.classList.add('location-hotspot-locked');
            btn.style.left = `${hotspot.x}%`;
            btn.style.top = `${hotspot.y}%`;
            // Audit D10: a11y — every hotspot gets an aria-label so screen-readers
            // announce both the destination and (when locked) why it's unavailable.
            // Title still set later for enabled; aria-label is the canonical SR text.
            {
                const _hsLabelKey = def.name.toLowerCase() + '.' + hotspot.id;
                const _hsTranslated = L.get(_hsLabelKey);
                const _hsLabel = (_hsTranslated !== _hsLabelKey) ? _hsTranslated : hotspot.label;
                const _hsReason = (isFullyLocked && disabledInfo && disabledInfo.reason) ? ` (locked: ${disabledInfo.reason})` : '';
                btn.setAttribute('aria-label', _hsLabel + _hsReason);
                if (isFullyLocked) btn.setAttribute('aria-disabled', 'true');
            }

            // Hotkey number (sequential among active hotspots)
            if (isInteractable) {
                _hotkeyCount++;
                if (_hotkeyCount <= 9) {
                    const numEl = document.createElement('span');
                    numEl.className = 'location-hotspot-num';
                    numEl.textContent = _hotkeyCount;
                    btn.appendChild(numEl);
                    btn.dataset.hotkey = _hotkeyCount;
                }
            }

            // Label with L10n support (try location.hotspotId key, fallback to hardcoded label)
            const labelEl = document.createElement('span');
            labelEl.className = 'location-hotspot-label';
            const l10nKey = def.name.toLowerCase() + '.' + hotspot.id;
            const translated = L.get(l10nKey);
            labelEl.textContent = (translated !== l10nKey) ? translated : hotspot.label;
            btn.appendChild(labelEl);

            // Disabled reason (shown below label ONLY when fully locked)
            if (isFullyLocked && disabledInfo.reason) {
                const reasonEl = document.createElement('span');
                reasonEl.className = 'location-hotspot-reason';
                reasonEl.textContent = disabledInfo.reason;
                btn.appendChild(reasonEl);
            }

            // "!" activity indicator when hotspot has multiple ENABLED actions
            const hasEnabledActivities = activities.some(a => a.isEnabled);
            if (isInteractable && hasEnabledActivities) {
                const indicator = document.createElement('span');
                indicator.className = 'location-hotspot-indicator';
                indicator.textContent = '!';
                btn.appendChild(indicator);
            }

            if (isInteractable) {
                btn.classList.add('location-hotspot-pulse');
                btn.title = hotspot.label;

                // Popover when the hotspot has ANY activities (enabled or disabled).
                // Disabled-only hotspots are already filtered out earlier by
                // `isInteractable`, so the only way to get here with an empty
                // activities list is via `isActive` (single direct-click choice).
                // The disabled-only-hotspot v0.2.5 fix renamed the original
                // `hasActivities` to `hasEnabledActivity` for the isInteractable
                // gate but left this site referencing the old name → ReferenceError.
                const hasActivities = activities.length > 0;
                if (hasActivities) {
                    // Multiple actions → show popover on click
                    const capturedHotspot = hotspot;
                    const capturedChoiceIdx = choiceIdx; // may be undefined
                    const capturedActivities = activities;
                    btn.addEventListener('click', (e) => {
                        e.stopPropagation();
                        const isMobile = window.innerWidth < 768;
                        if (isMobile) {
                            this._showActivityBottomSheet(capturedHotspot.label, capturedChoiceIdx, capturedActivities, choices);
                        } else {
                            this._showPopover(capturedHotspot, capturedChoiceIdx, capturedActivities, choices, btn);
                        }
                    });
                } else {
                    // Single action → direct click. Re-match by #hotspot: tag
                    // at click time so a refreshed choice list still routes
                    // correctly (e.g. choice text changes between renders).
                    const capturedId = hotspot.id;
                    const capturedChoiceIdx = choiceIdx;
                    btn.addEventListener('click', () => {
                        let currentIdx = -1;
                        const lastChoices = this._lastChoices || [];
                        for (const c of lastChoices) {
                            if (c.hotspot === capturedId && c.isEnabled) {
                                currentIdx = c.index;
                                break;
                            }
                        }
                        // No-tag choices don't render on pins; if Phase 0 had
                        // bound via the render-time map fall back to that.
                        if (currentIdx < 0) currentIdx = capturedChoiceIdx;
                        if (currentIdx === undefined || currentIdx < 0) return;
                        const isMobile = window.innerWidth < 768;
                        const isNavHotspotMobile = capturedId === 'leave' || capturedId === 'go_outside'
                            || this._activeLocation === 'Outside';
                        if (isMobile && !isNavHotspotMobile) {
                            this._showBottomSheet(hotspot.label, null, currentIdx);
                        } else {
                            this._deactivate();
                            // Always emit location:choiceSelected so main.js pre-action hooks
                            // (scroll-append clear, autosave breadcrumb, analytics) fire on
                            // BOTH desktop pin click + phone hotspot tap. Pre-fix (audit
                            // 2026-05-23): direct-storyManager call here skipped the event,
                            // so any new listener silently no-op'd on desktop pin clicks.
                            // main.js binds the listener at boot — selectChoice runs from
                            // there. (B5 architecture cleanup.)
                            events.emit('location:choiceSelected', currentIdx);
                        }
                    });
                }

                // (hotkey tracking done via _pendingHotkeys after sort)

                // Find the original choice for cost info + activity badge
                if (isActive) {
                    const originalChoice = choices.find(c => c.index === choiceIdx);
                    if (originalChoice && originalChoice.costText) {
                        const cost = document.createElement('span');
                        cost.className = 'location-hotspot-cost';
                        cost.textContent = originalChoice.costText;
                        btn.appendChild(cost);
                    }
                    // Activity badge (stat gains indicate interesting content)
                    if (originalChoice && (originalChoice.femGain > 0 || originalChoice.subGain > 0)) {
                        const badge = document.createElement('span');
                        badge.className = 'location-hotspot-activity';
                        badge.textContent = '\u2726';
                        btn.appendChild(badge);
                    }
                }
            }

            container.appendChild(btn);
        }

        // ── NPC Portraits (Apartment only) ────────────────────────────────
        // Standalone floating portrait elements, mirroring Unity's RenderNpcPortrait
        if (this._activeLocation === Location.HOME) {
            if (npcMasterEvents.length > 0) {
                const el = this._renderNpcPortrait('master', masterName, npcMasterEvents, choices);
                if (el) {
                    container.appendChild(el);
                    _hotkeyCount++;
                    if (_hotkeyCount <= 9) {
                        const numEl = document.createElement('span');
                        numEl.className = 'location-hotspot-num';
                        numEl.textContent = _hotkeyCount;
                        el.appendChild(numEl);
                    }
                    this._orderedHotspots.push({ element: el, choiceIdx: npcMasterEvents[0].index, hasActivities: npcMasterEvents.length > 1 });
                }
            }
            if (npcGirlfriendEvents.length > 0) {
                const el = this._renderNpcPortrait('girlfriend', girlfriendName, npcGirlfriendEvents, choices);
                if (el) {
                    container.appendChild(el);
                    _hotkeyCount++;
                    if (_hotkeyCount <= 9) {
                        const numEl = document.createElement('span');
                        numEl.className = 'location-hotspot-num';
                        numEl.textContent = _hotkeyCount;
                        el.appendChild(numEl);
                    }
                    this._orderedHotspots.push({ element: el, choiceIdx: npcGirlfriendEvents[0].index, hasActivities: npcGirlfriendEvents.length > 1 });
                }
            }
            if (npcWeekendEvents.length > 0) {
                const el = this._renderNpcPortrait('weekend', 'Weekend', npcWeekendEvents, choices);
                if (el) {
                    container.appendChild(el);
                    _hotkeyCount++;
                    if (_hotkeyCount <= 9) {
                        const numEl = document.createElement('span');
                        numEl.className = 'location-hotspot-num';
                        numEl.textContent = _hotkeyCount;
                        el.appendChild(numEl);
                    }
                    this._orderedHotspots.push({ element: el, choiceIdx: npcWeekendEvents[0].index, hasActivities: npcWeekendEvents.length > 1 });
                }
            }
        }

        // Unmatched choices rendered as a regular list below the map
        const unmatchedChoices = choices.filter(c => !allMatchedIndices.has(c.index));

        if (unmatchedChoices.length > 0) {
            const extraContainer = document.createElement('div');
            extraContainer.className = 'location-extra-choices';

            for (const choice of unmatchedChoices) {
                const btn = document.createElement('button');
                btn.className = 'choice-btn location-choice-btn';
                btn.textContent = choice.text;
                if (choice.isEnabled === false) {
                    // #disabled: choices without a #hotspot: tag land here.
                    // Without this branch they rendered as normal buttons whose
                    // click selected the Ink choice, which loops back to the hub:
                    // "I press the button and nothing happens" (foundby:Monki
                    // 0.3, Cabaret late-show volunteer variants).
                    btn.classList.add('disabled');
                    btn.setAttribute('aria-disabled', 'true');
                    if (choice.disabledReason) {
                        btn.title = choice.disabledReason;
                        btn.setAttribute('aria-label', `${choice.text}, locked: ${choice.disabledReason}`);
                        const reasonEl = document.createElement('div');
                        reasonEl.className = 'choice-disabled-reason';
                        reasonEl.textContent = choice.disabledReason;
                        btn.appendChild(reasonEl);
                    }
                } else {
                    btn.addEventListener('click', () => {
                        events.emit('location:choiceSelected', choice.index);
                    });
                }
                extraContainer.appendChild(btn);
            }

            container.appendChild(extraContainer);
        }

        // (hotkeys assigned inline during hotspot rendering above)

        this._root.appendChild(container);
        } catch (e) {
            console.error('[Locations] _render error:', e);
            // Restore story content so player isn't left with empty screen
            if (storyContent && savedContent) storyContent.innerHTML = savedContent;
            const ch = document.getElementById('choices');
            if (ch) ch.classList.remove('hidden');
        }

        // Bind keyboard navigation for number keys
        this._bindKeyboard();

        // Close popover on click outside. Remove any prior handler first —
        // _render is called on every re-render (tryActivate, language swap)
        // and without this guard the document-level listeners accumulate and
        // cause unrelated popovers to close on any click.
        if (this._onDocumentClick) {
            document.removeEventListener('click', this._onDocumentClick);
        }
        this._onDocumentClick = (e) => {
            if (this._activePopover && !this._activePopover.contains(e.target)) {
                this._closePopover();
            }
        };
        document.addEventListener('click', this._onDocumentClick);
    }

    /**
     * Mobile list navigation, replaces hotspot map with a vertical button list.
     */
    _renderMobileList(def, choices) {

        const container = document.createElement('div');
        container.className = 'location-container location-mobile-list';
        container.dataset.location = this._activeLocation;

        // Location header
        const header = document.createElement('div');
        header.className = 'location-mobile-header';
        const titleKey = def.name.toLowerCase() + '.title';
        const titleTranslated = L.get(titleKey);
        header.textContent = (titleTranslated !== titleKey) ? titleTranslated : def.name;
        container.appendChild(header);

        // Small background thumbnail (night variant if available)
        const isNightMobile = (() => { try { return this._storyManager?.currentStory?.variablesState['TimeSlot'] >= 3; } catch(e) { return false; } })();
        const bgImage = (this._activeLocation === Location.HOME && this._isLoft)
            ? def.loftBgImage : (isNightMobile && def.nightBgImage) ? def.nightBgImage : def.bgImage;
        const thumb = document.createElement('div');
        thumb.className = 'location-mobile-thumb';
        if (typeof isTarMode === 'function' && isTarMode()) {
            const blobUrl = typeof getImageUrl === 'function' ? getImageUrl(bgImage) : null;
            if (blobUrl) thumb.style.backgroundImage = `url(${blobUrl})`;
        } else {
            const folder = (window._imageManifest && window._imageManifest[bgImage]) || 'Locations';
            thumb.style.backgroundImage = `url(assets/images/${folder}/${bgImage}.png)`;
        }
        container.appendChild(thumb);

        // Build button list from hotspots
        const list = document.createElement('div');
        list.className = 'location-mobile-buttons';

        for (const hotspot of def.hotspots) {
            const choiceIdx = this._hotspotChoiceMap.get(hotspot.id);
            const activities = this._hotspotActivities.get(hotspot.id) || [];
            const isActive = choiceIdx !== undefined;
            const hasActivities = activities.length > 0;
            const isInteractable = isActive || hasActivities;
            // Hide undiscovered locations (no choice AND no disabled-choice);
            // time-gated closed spots have a #disabled choice + stay visible.
            if (!isInteractable && !this._hotspotDisabled.has(hotspot.id)) continue;

            const btn = document.createElement('button');
            btn.className = 'location-mobile-btn' + (isInteractable ? '' : ' disabled');

            // Label
            const l10nKey = def.name.toLowerCase() + '.' + hotspot.id;
            const translated = L.get(l10nKey);
            const label = (translated !== l10nKey) ? translated : hotspot.label;

            // Cost info
            let costText = '';
            if (isActive) {
                const originalChoice = choices.find(c => c.index === choiceIdx);
                if (originalChoice && originalChoice.costText) costText = originalChoice.costText;
            }

            // Disabled reason (#disabled: tag) — show why this hotspot isn't available.
            // Fall back to a neutral "not available right now" hint so disabled
            // cards always read as intentionally gated, never as broken.
            let reasonText = '';
            if (!isInteractable) {
                const disabledInfo = this._hotspotDisabled.get(hotspot.id);
                if (disabledInfo && disabledInfo.reason) {
                    reasonText = disabledInfo.reason;
                } else {
                    const fallback = L.get('hotspot.unavailable');
                    reasonText = (fallback && fallback !== 'hotspot.unavailable')
                        ? fallback
                        : 'Not available right now';
                }
                btn.classList.add('has-reason');
            }

            // Hotspot thumbnail (thumb = 'Folder/imagename'). Resolve the
            // appearance variant on the name part so masc/chastity/longhair/boobs
            // PCs get the matching tile (e.g. Beach/beach_swim → beach_swim_masc
            // trunks instead of the feminine pink-bikini base). resolveVariantName
            // only swaps when the variant exists, so tiles without one stay base.
            // Also tar-aware: the old direct assets/ URL 404'd in tar mode (itch).
            let thumbHtml = '';
            if (hotspot.thumb) {
                const slash = hotspot.thumb.lastIndexOf('/');
                const folder = slash >= 0 ? hotspot.thumb.slice(0, slash) : '';
                const baseName = slash >= 0 ? hotspot.thumb.slice(slash + 1) : hotspot.thumb;
                let resolvedName = baseName;
                try { resolvedName = appearanceState.resolveVariantName(baseName) || baseName; } catch (e) { resolvedName = baseName; }
                let bgUrl = null;
                if (typeof isTarMode === 'function' && isTarMode()) {
                    const blobUrl = getImageUrl(resolvedName) || getImageUrl(baseName);
                    if (blobUrl) bgUrl = `url(${blobUrl})`;
                } else {
                    const path = folder ? `${folder}/${resolvedName}` : resolvedName;
                    bgUrl = `url(assets/images/${path}.png)`;
                }
                if (bgUrl) {
                    thumbHtml = `<div class="location-mobile-btn-thumb" style="background-image:${bgUrl}"></div>`;
                    btn.classList.add('has-thumb');
                }
            }

            const reasonHtml = reasonText ? `<span class="location-mobile-btn-reason">${reasonText}</span>` : '';
            btn.innerHTML = `${thumbHtml}<span class="location-mobile-btn-label">${label}</span>${costText ? `<span class="location-mobile-btn-cost">${costText}</span>` : ''}${reasonHtml}${hasActivities ? '<span class="location-mobile-btn-indicator">!</span>' : ''}`;

            if (isInteractable) {
                const capturedId = hotspot.id;
                const capturedActivities = activities;
                const capturedChoiceIdx = choiceIdx;
                btn.addEventListener('click', () => {
                    if (hasActivities) {
                        this._showActivityBottomSheet(label, capturedChoiceIdx, capturedActivities, choices);
                    } else {
                        // Re-match by #hotspot: tag, fall back to render-time
                        // binding. Mobile previously also tried matchText
                        // fallback; removed v0.2.7 with universal tag coverage.
                        let currentIdx = -1;
                        const lastChoices = this._lastChoices || [];
                        for (const c of lastChoices) {
                            if (c.hotspot === capturedId && c.isEnabled) {
                                currentIdx = c.index;
                                break;
                            }
                        }
                        if (currentIdx < 0) currentIdx = capturedChoiceIdx;
                        if (currentIdx === undefined || currentIdx < 0) return;
                        this._deactivate();
                        // Always emit (B5 architecture cleanup 2026-05-23); main.js
                        // listener handles selectChoice + scroll-append clear.
                        events.emit('location:choiceSelected', currentIdx);
                    }
                });
            }

            list.appendChild(btn);
        }

        container.appendChild(list);

        // Unmatched choices (not tied to any hotspot)
        const allMatchedIndices = new Set();
        for (const [, idx] of this._hotspotChoiceMap) allMatchedIndices.add(idx);
        for (const [, acts] of this._hotspotActivities) {
            for (const a of acts) allMatchedIndices.add(a.index);
        }
        // Disabled choices with #hotspot: tags are on the map — exclude from unmatched
        for (const choice of choices) {
            if (choice.hotspot && this._hotspotDisabled.has(choice.hotspot)) {
                allMatchedIndices.add(choice.index);
            }
        }
        const unmatched = choices.filter(c => !allMatchedIndices.has(c.index));
        if (unmatched.length > 0) {
            for (const choice of unmatched) {
                const btn = document.createElement('button');
                btn.className = 'location-mobile-btn location-mobile-btn-extra';
                if (choice.isEnabled === false) {
                    // Same class as the desktop extras fix: hotspot-less
                    // #disabled: choices must not render as live buttons.
                    btn.classList.add('disabled', 'has-reason');
                    let reason = choice.disabledReason;
                    if (!reason) {
                        const fallback = L.get('hotspot.unavailable');
                        reason = (fallback && fallback !== 'hotspot.unavailable')
                            ? fallback
                            : 'Not available right now';
                    }
                    btn.innerHTML = `<span class="location-mobile-btn-label">${choice.text}</span><span class="location-mobile-btn-reason">${reason}</span>`;
                } else {
                    btn.innerHTML = `<span class="location-mobile-btn-label">${choice.text}</span>`;
                    btn.addEventListener('click', () => {
                        this._deactivate();
                        events.emit('location:choiceSelected', choice.index);
                    });
                }
                list.appendChild(btn);
            }
        }

        this._root.appendChild(container);
    }

    // _findCurrentChoiceIndex removed v0.2.7 — matchText click-time fallback
    // is no longer needed. Pin click resolution goes via #hotspot: tag in
    // _lastChoices only.

    _deactivate() {
        this._closePopover();
        this._unbindKeyboard();
        this._orderedHotspots = [];
        this._activeLocation = null;
        this._lastChoices = null;
        this._hotspotChoiceMap.clear();
        this._hotspotActivities.clear();
        if (this._onDocumentClick) {
            document.removeEventListener('click', this._onDocumentClick);
            this._onDocumentClick = null;
        }
        if (this._root) {
            this._root.classList.add('hidden');
            this._root.innerHTML = '';
        }
    }

    // ── DOM Construction ────────────────────────────────────────────────

    _createContainer() {
        const el = document.createElement('div');
        el.id = 'location-view';
        el.className = 'location-view-root hidden';
        // Insert at the TOP of story scroll area, before story-content
        const storyScroll = document.getElementById('story');
        const storyContent = document.getElementById('story-content');
        if (storyScroll && storyContent) {
            storyScroll.insertBefore(el, storyContent);
        } else if (storyScroll) {
            storyScroll.prepend(el);
        } else {
            document.body.appendChild(el);
        }
        return el;
    }

    // ── Popover (desktop) ────────────────────────────────────────────────

    _showPopover(hotspot, primaryChoiceIdx, activities, choices, anchorEl) {
        this._closePopover();

        const popover = document.createElement('div');
        popover.className = 'location-popover';

        // Position near the hotspot anchor
        const rect = anchorEl.getBoundingClientRect();
        const containerRect = this._root.getBoundingClientRect();
        const leftHalf = hotspot.x < 50;

        // Use fixed positioning relative to viewport
        popover.style.position = 'fixed';
        popover.style.top = `${rect.top}px`;
        if (leftHalf) {
            popover.style.left = `${rect.right + 8}px`;
        } else {
            popover.style.right = `${window.innerWidth - rect.left + 8}px`;
            // Anchor the --overlay-zoom scale (see _10-overlay-views.css) to the
            // pin-side corner so the popover grows into the screen, not off it.
            popover.style.transformOrigin = 'top right';
        }
        popover.style.zIndex = '500';

        // Title
        const title = document.createElement('div');
        title.className = 'location-popover-title';
        // Use translated label if available
        const popL10nKey = (this._currentLocDef?.name || '').toLowerCase() + '.' + hotspot.id;
        const popTranslated = L.get(popL10nKey);
        title.textContent = (popTranslated !== popL10nKey) ? popTranslated : hotspot.label;
        popover.appendChild(title);

        let popNum = 1;

        // Main hotspot action (if available) — use choice text, not hotspot label
        if (primaryChoiceIdx !== undefined) {
            const mainChoice = choices.find(c => c.index === primaryChoiceIdx);
            const mainLabel = mainChoice ? mainChoice.text : hotspot.label;
            const mainBtn = this._createPopoverItem(mainLabel, mainChoice, primaryChoiceIdx, popNum, true);
            popover.appendChild(mainBtn);
            popNum++;
        }

        // Activity items
        for (const act of activities) {
            const actBtn = this._createPopoverItem(act.text, act, act.index, popNum, false);
            popover.appendChild(actBtn);
            popNum++;
        }

        // Stop clicks inside popover from closing it
        popover.addEventListener('click', (e) => e.stopPropagation());

        document.body.appendChild(popover);
        this._activePopover = popover;

        // Adjust if it goes off-screen vertically
        requestAnimationFrame(() => {
            const pRect = popover.getBoundingClientRect();
            if (pRect.bottom > window.innerHeight - 10) {
                popover.style.top = `${window.innerHeight - pRect.height - 10}px`;
            }
        });
    }

    _createPopoverItem(label, choice, choiceIdx, number, isMain) {
        const item = document.createElement('button');
        item.className = 'location-popover-item' + (isMain ? ' location-popover-item--main' : '');

        if (choice && !choice.isEnabled) {
            item.disabled = true;
            item.classList.add('location-popover-item--disabled');
            if (choice.disabledReason) item.title = choice.disabledReason;
        }

        // Number badge
        const badge = document.createElement('span');
        badge.className = 'location-popover-badge';
        badge.textContent = number;
        item.appendChild(badge);

        // Label text
        const labelEl = document.createElement('span');
        labelEl.className = 'location-popover-text';
        labelEl.textContent = label;
        item.appendChild(labelEl);

        // Disabled reason (after label)
        if (choice && !choice.isEnabled && choice.disabledReason) {
            const reasonEl = document.createElement('span');
            reasonEl.className = 'location-popover-reason';
            reasonEl.textContent = choice.disabledReason;
            item.appendChild(reasonEl);
        }

        // Cost + stat badges
        if (choice) {
            const meta = document.createElement('span');
            meta.className = 'location-popover-meta';
            if (choice.costText) {
                const costEl = document.createElement('span');
                costEl.className = 'location-popover-cost';
                costEl.textContent = choice.costText;
                meta.appendChild(costEl);
            }
            const story = this._storyManager?.currentStory;
            // Pacing 2.0: raw tag → single wrapper application (see choices.js).
            const rawFem = choice.femTag !== undefined && choice.femTag !== -1 ? choice.femTag : choice.femGain;
            const rawSub = choice.subTag !== undefined && choice.subTag !== -1 ? choice.subTag : choice.subGain;
            if (rawFem > 0) {
                const eff = getEffectiveFemGain(rawFem, story);
                if (eff > 0) {
                    const femEl = document.createElement('span');
                    femEl.className = 'choice-badge badge-fem';
                    femEl.textContent = `+${eff} Fem`;
                    meta.appendChild(femEl);
                }
            }
            if (rawSub > 0) {
                const eff = getEffectiveSubGain(rawSub, story);
                if (eff > 0) {
                    const subEl = document.createElement('span');
                    subEl.className = 'choice-badge badge-sub';
                    subEl.textContent = `+${eff} Sub`;
                    meta.appendChild(subEl);
                }
            }
            if (meta.childNodes.length > 0) item.appendChild(meta);
        }

        item.addEventListener('click', (e) => {
            e.stopPropagation();
            this._closePopover();
            events.emit('location:choiceSelected', choiceIdx);
        });

        return item;
    }

    // ── NPC Portrait Rendering (Apartment) ──────────────────────────────

    /**
     * Render a standalone floating NPC portrait on the apartment map.
     * Mirrors Unity's RenderNpcPortrait: circular avatar + label, clickable.
     * @param {string} npcId - 'master' or 'girlfriend'
     * @param {string} displayName - NPC display name
     * @param {Array} npcChoices - choices related to this NPC
     * @param {Array} allChoices - all choices (for popover)
     * @returns {HTMLElement|null}
     */
    _renderNpcPortrait(npcId, displayName, npcChoices, allChoices) {
        const config = NPC_PORTRAIT_CONFIG[npcId];
        if (!config) return null;

        const wrapper = document.createElement('div');
        wrapper.className = `location-npc-portrait ${config.cssClass}`;
        wrapper.style.left = `${config.x}%`;
        wrapper.style.top = `${config.y}%`;

        // Circular portrait image
        const img = document.createElement('img');
        img.className = 'location-npc-portrait-img';
        const blobUrl = (typeof isTarMode === 'function' && isTarMode() && typeof getImageUrl === 'function') ? getImageUrl(config.portrait) : null;
        img.src = blobUrl || `assets/images/Portraits/${config.portrait}.png`;
        img.alt = displayName || npcId;
        img.onerror = () => { img.style.display = 'none'; };
        wrapper.appendChild(img);

        // Label (first choice text, with "+" if multiple)
        const label = document.createElement('span');
        label.className = 'location-npc-portrait-label';
        label.textContent = npcChoices.length > 1
            ? `${npcChoices[0].text} +`
            : npcChoices[0].text;
        wrapper.appendChild(label);

        // Click behavior
        if (npcChoices.length > 1) {
            // Multiple NPC events → show popover
            wrapper.addEventListener('click', (e) => {
                e.stopPropagation();
                const isMobile = window.innerWidth < 768;
                if (isMobile) {
                    this._showNpcBottomSheet(displayName || npcId, npcChoices);
                } else {
                    this._showNpcPopover(config, displayName || npcId, npcChoices, wrapper);
                }
            });
        } else {
            // Single NPC event → direct click
            const idx = npcChoices[0].index;
            wrapper.addEventListener('click', (e) => {
                e.stopPropagation();
                events.emit('location:choiceSelected', idx);
            });
        }

        // Pulse animation via CSS class toggle
        wrapper.classList.add('location-npc-portrait-pulse');

        return wrapper;
    }

    /**
     * Show popover for NPC with multiple events (desktop).
     */
    _showNpcPopover(config, displayName, npcChoices, anchorEl) {
        this._closePopover();

        const popover = document.createElement('div');
        popover.className = 'location-popover';

        const rect = anchorEl.getBoundingClientRect();
        popover.style.position = 'fixed';
        popover.style.top = `${rect.top}px`;
        popover.style.left = `${rect.right + 8}px`;
        popover.style.zIndex = '500';

        const title = document.createElement('div');
        title.className = 'location-popover-title';
        title.textContent = displayName;
        popover.appendChild(title);

        let popNum = 1;
        for (const choice of npcChoices) {
            const item = this._createPopoverItem(choice.text, choice, choice.index, popNum, popNum === 1);
            popover.appendChild(item);
            popNum++;
        }

        popover.addEventListener('click', (e) => e.stopPropagation());
        document.body.appendChild(popover);
        this._activePopover = popover;

        requestAnimationFrame(() => {
            const pRect = popover.getBoundingClientRect();
            if (pRect.bottom > window.innerHeight - 10) {
                popover.style.top = `${window.innerHeight - pRect.height - 10}px`;
            }
            if (pRect.right > window.innerWidth - 10) {
                popover.style.left = 'auto';
                popover.style.right = `${window.innerWidth - rect.left + 8}px`;
                // Re-anchor the --overlay-zoom scale origin to the new side.
                popover.style.transformOrigin = 'top right';
            }
        });
    }

    /**
     * Show bottom sheet for NPC with multiple events (mobile).
     */
    _showNpcBottomSheet(displayName, npcChoices) {
        document.querySelector('.location-bottom-sheet')?.remove();

        const sheet = document.createElement('div');
        sheet.className = 'location-bottom-sheet';

        let html = `
            <div class="bottom-sheet-handle"></div>
            <div class="bottom-sheet-title">${displayName}</div>
            <div class="bottom-sheet-actions">
        `;

        for (const choice of npcChoices) {
            const costText = choice.costText || '';
            const disabled = choice.isEnabled === false ? ' disabled' : '';
            const reasonHtml = (!choice.isEnabled && choice.disabledReason) ? `<span class="bottom-sheet-action-reason">${choice.disabledReason}</span>` : '';
            html += `<button class="bottom-sheet-action"${disabled} data-idx="${choice.index}">
                <span class="bottom-sheet-action-label">${choice.text}</span>
                ${reasonHtml}
                ${costText ? `<span class="bottom-sheet-action-cost">${costText}</span>` : ''}
            </button>`;
        }

        html += `</div>
            <button class="bottom-sheet-cancel" id="bs-cancel">${L.get('action.cancel') || 'Cancel'}</button>
        `;
        sheet.innerHTML = html;
        document.body.appendChild(sheet);
        requestAnimationFrame(() => sheet.classList.add('bottom-sheet-open'));

        sheet.querySelectorAll('.bottom-sheet-action').forEach(btn => {
            btn.addEventListener('click', () => {
                const idx = parseInt(btn.dataset.idx, 10);
                sheet.remove();
                events.emit('location:choiceSelected', idx);
            });
        });

        sheet.querySelector('#bs-cancel').addEventListener('click', () => {
            sheet.classList.remove('bottom-sheet-open');
            setTimeout(() => sheet.remove(), 300);
        });
    }

    _closePopover() {
        if (this._activePopover) {
            this._activePopover.remove();
            this._activePopover = null;
        }
        // Also clean up any lingering bottom sheets
        document.querySelector('.location-bottom-sheet')?.remove();
    }

    // ── Activity Bottom Sheet (mobile, multiple actions) ──────────────────

    _showActivityBottomSheet(label, primaryChoiceIdx, activities, choices) {
        document.querySelector('.location-bottom-sheet')?.remove();

        const sheet = document.createElement('div');
        sheet.className = 'location-bottom-sheet';

        let html = `
            <div class="bottom-sheet-handle"></div>
            <div class="bottom-sheet-title">${label}</div>
            <div class="bottom-sheet-actions">
        `;

        // Main action (use choice text, not hotspot label, to avoid duplication)
        if (primaryChoiceIdx !== undefined) {
            const mainChoice = choices.find(c => c.index === primaryChoiceIdx);
            const mainLabel = mainChoice?.text || label;
            const costText = mainChoice?.costText || '';
            const disabled = mainChoice?.isEnabled === false ? ' disabled' : '';
            const reasonText = mainChoice?.isEnabled === false ? (mainChoice?.disabledReason || '') : '';
            html += `<button class="bottom-sheet-action"${disabled} data-idx="${primaryChoiceIdx}">
                <span class="bottom-sheet-action-label">${mainLabel}</span>
                ${costText ? `<span class="bottom-sheet-action-cost">${costText}</span>` : ''}
                ${reasonText ? `<span class="bottom-sheet-action-reason">${reasonText}</span>` : ''}
            </button>`;
        }

        // Activity actions
        for (const act of activities) {
            const costText = act.costText || '';
            const disabled = act.isEnabled === false ? ' disabled' : '';
            const reasonText = act.isEnabled === false ? (act.disabledReason || '') : '';
            html += `<button class="bottom-sheet-action"${disabled} data-idx="${act.index}">
                <span class="bottom-sheet-action-label">${act.text}</span>
                ${costText ? `<span class="bottom-sheet-action-cost">${costText}</span>` : ''}
                ${reasonText ? `<span class="bottom-sheet-action-reason">${reasonText}</span>` : ''}
            </button>`;
        }

        html += `</div>
            <button class="bottom-sheet-cancel" id="bs-cancel">${L.get('action.cancel') || 'Cancel'}</button>
        `;
        sheet.innerHTML = html;
        document.body.appendChild(sheet);
        requestAnimationFrame(() => sheet.classList.add('bottom-sheet-open'));

        // Wire up action buttons
        sheet.querySelectorAll('.bottom-sheet-action').forEach(btn => {
            btn.addEventListener('click', () => {
                const idx = parseInt(btn.dataset.idx, 10);
                sheet.remove();
                events.emit('location:choiceSelected', idx);
            });
        });

        sheet.querySelector('#bs-cancel').addEventListener('click', () => {
            sheet.classList.remove('bottom-sheet-open');
            setTimeout(() => sheet.remove(), 300);
        });
    }

    // ── Single-action Bottom Sheet (mobile, legacy) ───────────────────────

    _showBottomSheet(label, choice, choiceIdx) {
        // Remove existing
        document.querySelector('.location-bottom-sheet')?.remove();

        const sheet = document.createElement('div');
        sheet.className = 'location-bottom-sheet';
        const costText = choice?.costText || '';
        const badges = [];
        // Bug 4 sibling fix (2026-05-27): use wrapper-effective value, skip on 0.
        // Pre-fix the bottom sheet showed `+${choice.femGain}` (raw tag base)
        // even when fem_gain()'s diminishing-returns clamp delivered 0 at high
        // Fem. Now mirrors the popover renderer above.
        const _story = this._storyManager?.currentStory;
        // Pacing 2.0: raw tag → single wrapper application (see choices.js).
        const _rawFem = choice?.femTag !== undefined && choice?.femTag !== -1 ? choice.femTag : choice?.femGain;
        const _rawSub = choice?.subTag !== undefined && choice?.subTag !== -1 ? choice.subTag : choice?.subGain;
        if (_rawFem > 0) {
            const eff = getEffectiveFemGain(_rawFem, _story);
            if (eff > 0) badges.push(`<span class="choice-badge badge-fem">+${eff} Fem</span>`);
        }
        if (_rawSub > 0) {
            const eff = getEffectiveSubGain(_rawSub, _story);
            if (eff > 0) badges.push(`<span class="choice-badge badge-sub">+${eff} Sub</span>`);
        }
        sheet.innerHTML = `
            <div class="bottom-sheet-handle"></div>
            <div class="bottom-sheet-title">${label}</div>
            ${costText ? `<div class="bottom-sheet-cost">${costText}</div>` : ''}
            ${badges.length ? `<div class="bottom-sheet-badges">${badges.join('')}</div>` : ''}
            <button class="bottom-sheet-go" id="bs-go">${L.get('action.go') || 'Go'}</button>
            <button class="bottom-sheet-cancel" id="bs-cancel">${L.get('action.cancel') || 'Cancel'}</button>
        `;
        document.body.appendChild(sheet);
        requestAnimationFrame(() => sheet.classList.add('bottom-sheet-open'));

        sheet.querySelector('#bs-go').addEventListener('click', () => {
            sheet.remove();
            events.emit('location:choiceSelected', choiceIdx);
        });
        sheet.querySelector('#bs-cancel').addEventListener('click', () => {
            sheet.classList.remove('bottom-sheet-open');
            setTimeout(() => sheet.remove(), 300);
        });
    }
}

/** @type {LocationsUI} */
export { LOCATION_DEFS };
export const locationsUI = new LocationsUI();
